import { Form } from 'antd';
import { AccountComponent } from 'components/AccountComponent';
import { QRCodeSetup } from 'components/AccountComponent/qr-code-setup';
import { Rights } from 'components/UsersComponent/rights';
import { MessageStates } from 'containers/MessageBox';
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  addPasswordRequest,
  changeOwnerRequest,
  changePasswordRequest,
  deleteAccountRequest,
  disableMfaRequest,
  disconnectProviderRequest,
  enableMfaRequest,
  fetchConnectUrlRequest,
  fetchProfilePictureRequest,
  fetchQRCodeRequest,
  updateDetailsRequest,
  uploadProfilePictureRequest,
} from 'store/account/actions';
import {
  getAccountLoading,
  getAccountQRCodeData,
} from 'store/account/selectors';
import {
  AccountType,
  Currency,
  Provider,
  TablePermission,
} from 'store/api/enums';
import {
  turnDrawerOff,
  turnDrawerOn,
  turnMessageOn,
  turnModalOff,
  turnModalOn,
} from 'store/app/actions';
import {
  CurrentModal,
  DrawerType,
  LicenseType,
  WindowType,
} from 'store/app/enums';
import {
  getMenuSize,
  getModalOpen,
  getProfilePictures,
} from 'store/app/selectors';
import { ACCESS_RIGHTS_DRAWER_WIDTH } from 'store/app/constants';
import { DrawerData, MessageData, ModalData } from 'store/app/types';
import { User } from 'store/auth/models';
import { getAuthUser, getAuthUserLicence } from 'store/auth/selectors';
import {
  fetchCountriesExternalDataRequest,
  fetchTimezonesRequest,
} from 'store/common/actions';
import {
  getCommonCountries,
  getCommonLoading,
  getCommonTimezones,
  getCountriesExternalData,
} from 'store/common/selectors';
import { getOrgMappedOrganisations } from 'store/organisation/selectors';
import {
  deleteUserRequest,
  fetchUserHistoryRequest,
  fetchUserRightsRequest,
  fetchUsersRequest,
  setAllUserRightsRequest,
  setUserRightsRequest,
} from 'store/user/actions';
import {
  getUserHistory,
  getUserLoading,
  getUserMembers,
  getUserRights,
} from 'store/user/selectors';
import PicPage from './pic-page';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PwaMorePage } from 'pwa/components/PwaMorePage';
import { PwaPersonalDetailsPage } from 'pwa/components/PwaPersonalDetailsPage';
import { PwaSecurityPage } from 'pwa/components/PwaSecurityPage';
import { PwaLoginHistoryPage } from 'pwa/components/PwaLoginHistoryPage';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { ContactDetailsForm } from 'components/AccountComponent/contact-details-form';
import { ChangeOwner } from 'components/AccountComponent/change-owner';
import { UserRights } from 'store/user/models';
import routes from 'routes';
import { checkPageLoaded } from 'utils/check-page-loaded';
import {
  getBillingDetails,
  getCurrentSubscription,
  getTaxNumberValidation,
  //getSubscriptionPlanTypes,
} from 'store/billing/selectors';
import {
  fetchBillingDetailsRequest,
  saveBillingDetailsRequest,
  validateVATNumberRequest,
  validateVATNumberSuccess,
} from 'store/billing/actions';
import { BillingFormRequest } from 'store/billing/models/billingFormRequest';
import Features from 'store/billing/models/features.json';
import { SubscriptionPlan } from 'store/api/enums';
import { Plan } from 'store/billing/models';

export interface StaffMember {
  id: string;
  name: string;
}

export const AccountPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const pwaMobileNav = usePwaMobileNavEnabled();
  const pwaView = searchParams.get('view');
  const parameters: any = useParams();
  const accountId = parameters.accountId;
  const errorId = parameters.errorId;
  const currentUser = useSelector(getAuthUser);
  const commonLoading = useSelector(getCommonLoading);
  const userLoading = useSelector(getUserLoading);
  const accountLoading = useSelector(getAccountLoading);
  const timezones = useSelector(getCommonTimezones);
  const userHistory = useSelector(getUserHistory);
  const QRCodeData = useSelector(getAccountQRCodeData);
  const modalOpen = useSelector(getModalOpen);
  const members = useSelector(getUserMembers);
  const loading = commonLoading || userLoading || accountLoading;
  const rights = useSelector(getUserRights);
  const mappedOrganisations = useSelector(getOrgMappedOrganisations);
  const countriesExternalData = useSelector(getCountriesExternalData);
  const countries = useSelector(getCommonCountries);
  //const subscriptionPlanTypes = useSelector(getSubscriptionPlanTypes);
  const menuSize = useSelector(getMenuSize);
  //const taxNumberValidation = useSelector(getTaxNumberValidation);
  const billingDetails = useSelector(getBillingDetails);
  const currentSubscription = useSelector(getCurrentSubscription);
  const userLicence = useSelector(getAuthUserLicence);
  const profilePictures = useSelector(getProfilePictures);
  //const [formPassword] = Form.useForm();
  const [formContactDetails] = Form.useForm();
  const [staffMembers, setStaffMembers] = React.useState<StaffMember[]>([]);
  const [currentModal, setCurrentModal] = React.useState<CurrentModal>(
    CurrentModal.NONE
  );

  const [selectedAccount, setSelectedAccount] = React.useState<User>();

  const { t } = useTranslation();

  const saveUserRights = (
    userRights: UserRights,
    tablePermission: TablePermission
  ) => {
    const accountUuid = userRights.accountUuid;
    const userToModify = members?.find((m) => m.accountUuid === accountUuid);
    const userToSend = userToModify ? userToModify : undefined;

    dispatch(setUserRightsRequest(userRights, tablePermission, userToSend));
  };

  const saveAllUserRights = (
    userRights: UserRights[],
    tablePermission: TablePermission
  ) => {
    const accountUuid = userRights[0].accountUuid;
    const userToModify = members?.find((m) => m.accountUuid === accountUuid);
    const userToSend = userToModify ? userToModify : undefined;

    dispatch(setAllUserRightsRequest(userRights, tablePermission, userToSend));
  };

  const uploadImage = (base64: File) => {
    currentUser &&
      dispatch(uploadProfilePictureRequest(base64, currentUser?.userUuid));
    dispatch(turnModalOff());
  };

  const openAccessRights = (accountId) => {
    dispatch(fetchUserRightsRequest(accountId));
    setCurrentModal(CurrentModal.RIGHTS);
  };

  const openEditDetails = () => {
    const modalData: DrawerData = {
      type: DrawerType.EDIT,
      title: t('organisation.edit-details'),
      component: (
        <ContactDetailsForm
          updateContact={updateContact}
          form={formContactDetails}
          timezones={timezones}
          countriesExternalData={countriesExternalData}
        />
      ),
    };

    dispatch(turnDrawerOn(modalData));
  };

  const openChangeOwnerForm = () => {
    const modalData: DrawerData = {
      type: DrawerType.CHANGE_OWNER,
      title: t('users.change-owner'),
      component: (
        <ChangeOwner updateOwner={updateOwner} staffMembers={staffMembers} />
      ),
    };

    dispatch(turnDrawerOn(modalData));
  };

  const savePassword = (
    oldPassword: string,
    newPassword: string,
    newPasswordConfirm: string
  ) => {
    if (!currentUser?.hasPassword && newPassword === newPasswordConfirm) {
      dispatch(addPasswordRequest(newPassword));
    } else if (oldPassword === newPassword) {
      const message: MessageData = {
        title: t('account.change-password-failed'),
        description: t('account.old-and-new-password-cannot-be-the-same'),
        type: MessageStates.ERROR,
      };
      dispatch(turnMessageOn(message));
    } else if (newPassword === newPasswordConfirm) {
      dispatch(changePasswordRequest(oldPassword, newPassword));
    } else {
      const message: MessageData = {
        title: t('account.change-password-failed'),
        description: t('account.retyped-password-does-not-match-new-password'),
        type: MessageStates.ERROR,
      };
      dispatch(turnMessageOn(message));
    }
  };

  const openEditImageWindow = (mode: 'preview' | 'edit' = 'edit') => {
    const modalData: ModalData = {
      type: WindowType.IMAGE,
      title:
        mode === 'preview'
          ? t('common.preview')
          : t('common.edit-profile-picture'),
      component: (
        <PicPage
          profilePicture={
            mode === 'preview' ? selectedAccount?.profilePicture : undefined
          }
          uploadImage={uploadImage}
          onClose={() => dispatch(turnModalOff())}
          mode={mode}
        />
      ),
    };

    dispatch(turnModalOn(modalData));
  };

  const onVerifyCode = (code: string) => {
    const secretKey = QRCodeData?.secretKey ? QRCodeData?.secretKey : '';
    dispatch(enableMfaRequest(code, secretKey));
  };

  const openMFAWindow = () => {
    const modalData: DrawerData = {
      type: DrawerType.CHANGE_OWNER,
      title: t('account.set-up-authenticator-app'),
      component: (
        <QRCodeSetup QRCodeData={QRCodeData} onVerifyCode={onVerifyCode} />
      ),
    };

    dispatch(turnDrawerOn(modalData));
  };

  const deleteUser = (userToDeleteId: string) => {
    currentUser &&
      userToDeleteId &&
      dispatch(deleteUserRequest(userToDeleteId));
  };

  React.useEffect(() => {
    if (rights && currentModal === CurrentModal.RIGHTS) {
      const modalData: DrawerData = {
        type: DrawerType.RIGHTS,
        title: t('users.access-rights'),
        width: ACCESS_RIGHTS_DRAWER_WIDTH,
        component: !loading && rights && (
          <Rights
            loading={loading}
            rights={rights}
            saveUserRights={saveUserRights}
            saveAllUserRights={saveAllUserRights}
            entities={mappedOrganisations}
            onClose={() => dispatch(turnDrawerOff())}
          />
        ),
      };

      dispatch(turnDrawerOn(modalData));
    }
  }, [rights]);

  /* React.useEffect(() => {
    modalOpen && formPassword.resetFields();
  }, [modalOpen]); */

  React.useEffect(() => {
    const staffMembers: StaffMember[] = [];
    if (members) {
      members.map((m) => {
        if (
          AccountType[m.accountType] === AccountType.STAFF &&
          m.accountActivated
        ) {
          staffMembers.push({
            id: m.accountUuid,
            name: `${m.lastName} ${m.firstName}`,
          });
        }
      });
    }

    setStaffMembers(staffMembers);
  }, [members]);

  React.useEffect(() => {
    let account: User | undefined;
    if (accountId) {
      account = members?.find((member) => member.accountUuid == accountId);
      // Members may not have loaded yet on a refresh — fall back to currentUser
      // if the accountId already matches the logged-in user's account
      if (!account && currentUser?.accountUuid === accountId) {
        account = currentUser;
      }
    } else {
      account = currentUser;
    }

    if (account) {
      // Check if profile picture exists in app state and merge it
      if (profilePictures && account.userUuid) {
        const profilePic = profilePictures.find(
          (pp) => pp?.userUuid === account?.userUuid
        );
        if (profilePic?.profilePicture && !account.profilePicture) {
          account = {
            ...account,
            profilePicture: profilePic.profilePicture,
          };
        }
      }
      setSelectedAccount(account);
    }
  }, [accountId, members, currentUser, profilePictures]);

  React.useEffect(() => {
    formContactDetails.setFieldsValue(selectedAccount);

    if (selectedAccount?.hasProfilePicture && !selectedAccount.profilePicture)
      dispatch(fetchProfilePictureRequest(selectedAccount?.userUuid));
  }, [selectedAccount]);

  // Update selectedAccount with profile picture from app state when it becomes available
  React.useEffect(() => {
    if (!selectedAccount?.userUuid || !profilePictures) return;

    const profilePic = profilePictures.find(
      (pp) => pp?.userUuid === selectedAccount.userUuid
    );

    if (
      profilePic?.profilePicture &&
      profilePic.profilePicture !== selectedAccount.profilePicture
    ) {
      setSelectedAccount((prev) => {
        if (prev?.userUuid === selectedAccount.userUuid) {
          return {
            ...prev,
            profilePicture: profilePic.profilePicture,
          };
        }
        return prev;
      });
    }
  }, [
    profilePictures,
    selectedAccount?.userUuid,
    selectedAccount?.profilePicture,
  ]);

  const connectXeroAccount = () => {
    const providerFormattedName = t(`common.${Provider.XERO?.toLowerCase()}`, {
      defaultValue: Provider.XERO,
    });
    dispatch(
      fetchConnectUrlRequest(Provider.XERO, 'linkxero', providerFormattedName)
    );
  };

  const connectQboAccount = () => {
    const providerFormattedName = t(`common.${Provider.QUICKBOOKS?.toLowerCase()}`, {
      defaultValue: Provider.QUICKBOOKS,
    });
    dispatch(
      fetchConnectUrlRequest(
        Provider.QUICKBOOKS,
        'linkquickbook',
        providerFormattedName
      )
    );
  };

  const connectZohoAccount = () => {
    const providerFormattedName = t(`common.${Provider.ZOHO?.toLowerCase()}`, {
      defaultValue: Provider.ZOHO,
    });
    dispatch(
      fetchConnectUrlRequest(Provider.ZOHO, 'linkzoho', providerFormattedName)
    );
  };

  const disconnectXeroAccount = () => {
    dispatch(
      disconnectProviderRequest(
        Provider.XERO,
        t(`common.${Provider.XERO?.toLowerCase()}`, {
          defaultValue: Provider.XERO,
        })
      )
    );
  };

  const disconnectQboAccount = () => {
    dispatch(
      disconnectProviderRequest(
        Provider.QUICKBOOKS,
        t(`common.${Provider.QUICKBOOKS?.toLowerCase()}`, {
          defaultValue: Provider.QUICKBOOKS,
        })
      )
    );
  };

  const disconnectZohoAccount = () => {
    dispatch(
      disconnectProviderRequest(
        Provider.ZOHO,
        t(`common.${Provider.ZOHO?.toLowerCase()}`, {
          defaultValue: Provider.ZOHO,
        })
      )
    );
  };

  const disconnectGoogleAccount = () => {
    dispatch(
      disconnectProviderRequest(
        Provider.GOOGLE,
        t(`common.${Provider.GOOGLE?.toLowerCase()}`, {
          defaultValue: Provider.GOOGLE,
        })
      )
    );
  };

  //const disconnectMicrosoftAccount = () => {
  //  const providerFormattedName = t(`common.${Provider.AZURE?.toLowerCase()}`, {
  //    defaultValue: Provider.AZURE,
  //  });
  //  dispatch(disconnectProviderRequest(Provider.AZURE, providerFormattedName));
  // };

  const removeMfa = (userId: string) => {
    dispatch(disableMfaRequest(userId));
  };

  const deleteProfile = () => {
    dispatch(deleteAccountRequest());
  };

  const updateContact = (user: User) => {
    dispatch(updateDetailsRequest(user));
    dispatch(turnDrawerOff());
  };

  const updateOwner = (accountId: string) => {
    dispatch(changeOwnerRequest(accountId));
    dispatch(turnDrawerOff());
  };

  const redirectPage = (url: string) => {
    navigate(url);
  };

  const saveBillingDetails = (billingDetails: BillingFormRequest) => {
    dispatch(
      saveBillingDetailsRequest(currentUser?.accountUuid ?? '', billingDetails)
    );
  };

  useEffect(() => {
    dispatch(fetchTimezonesRequest());
    dispatch(fetchQRCodeRequest());
    dispatch(fetchCountriesExternalDataRequest());
    if (!billingDetails && currentUser) {
      dispatch(fetchBillingDetailsRequest(currentUser?.accountUuid));
    }

    currentUser && dispatch(fetchUserHistoryRequest(currentUser?.userUuid));
    if (!members && !userLoading) {
      dispatch(fetchUsersRequest());
    }
    const didShow = localStorage.getItem('usedMessageShown');
    if (errorId && errorId === 'linkused') {
      if (!didShow) {
        localStorage.setItem('usedMessageShown', 'true');
        const message: MessageData = {
          title: t('account.connection-problem'),
          description: t('account.xero-account-already-connected'),
          type: MessageStates.ERROR,
        };
        dispatch(turnMessageOn(message));
        navigate(routes.account.root);
      } else {
        localStorage.removeItem('usedMessageShown');
      }
    }
    currentUser && checkPageLoaded(location.pathname, routes.account.root);
  }, []);

  const changeCurrency = (newCurrency: Currency) => {
    /*       dispatch(
        setCurrentPlan(selectedPlan.currentPlan, newCurrency, selectedPlan.price)
      ); */
  };

  /* const validateVatNumber = (countryCode: string, taxNumber: string) => {
    dispatch(validateVATNumberRequest(countryCode, taxNumber));
  }; */

  /* const resetValidationVatNumber = () => {
    dispatch(validateVATNumberSuccess(false));
  }; */

  const getFeaturesOfPlans = () => {
    const connectedOrganisationsFeature = Features.find(
      (fet) => fet.feature?.toLowerCase() === 'free entities included'
    );

    const priceFeature = Features.find(
      (fet) => fet.feature?.toLowerCase() === 'price'
    );

    const plans = Object.values(SubscriptionPlan).filter(
      (plan) => plan != SubscriptionPlan.FREE_PLAN
    );

    return plans.map((planType) => {
      const planSize = planType.split(' ')[0]?.toLowerCase();
      const plan: Plan = {
        planName: planType,
        connectedOrganisations: connectedOrganisationsFeature
          ? connectedOrganisationsFeature[planSize]
          : undefined,
        price: priceFeature ? priceFeature[planSize] : undefined,
        vatNumberCheckerAddon: 2,
        additionalOrganisations: 2,
        totalAmount: priceFeature ? priceFeature[planSize] : undefined,
      };
      return plan;
    });
  };

  if (pwaMobileNav && !accountId && !errorId) {
    if (pwaView === 'details') {
      return (
        <React.Fragment>
          <Helmet>
            <title>{`${t('users.personal-details', { defaultValue: 'Personal details' })} | ${process.env.PROJECT_NAME}`}</title>
          </Helmet>
          <PwaPersonalDetailsPage />
        </React.Fragment>
      );
    }
    if (pwaView === 'security') {
      return (
        <React.Fragment>
          <Helmet>
            <title>{`${t('users.account-security', { defaultValue: 'Account & security' })} | ${process.env.PROJECT_NAME}`}</title>
          </Helmet>
          <PwaSecurityPage />
        </React.Fragment>
      );
    }
    if (pwaView === 'history') {
      return (
        <React.Fragment>
          <Helmet>
            <title>{`${t('users.login-history', { defaultValue: 'Login history' })} | ${process.env.PROJECT_NAME}`}</title>
          </Helmet>
          <PwaLoginHistoryPage />
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        <Helmet>
          <title>{`${t('navigation.more', { defaultValue: 'More' })} | ${process.env.PROJECT_NAME}`}</title>
        </Helmet>
        <PwaMorePage />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>{`${t('account.page-title')} | ${process.env.PROJECT_NAME}`}</title>
        <meta name="description" content={t('account.page-meta-description')} />
      </Helmet>
      <AccountComponent
        loading={loading}
        currentUser={currentUser}
        history={userHistory}
        connectXeroAccount={connectXeroAccount}
        connectQboAccount={connectQboAccount}
        connectZohoAccount={connectZohoAccount}
        disconnectXeroAccount={disconnectXeroAccount}
        disconnectQboAccount={disconnectQboAccount}
        disconnectZohoAccount={disconnectZohoAccount}
        disconnectGoogleAccount={disconnectGoogleAccount}
        openMFAWindow={openMFAWindow}
        removeMfa={removeMfa}
        deleteProfile={deleteProfile}
        savePassword={savePassword}
        openAccessRights={openAccessRights}
        openEditImageWindow={openEditImageWindow}
        selectedAccount={selectedAccount}
        onDeleteUser={deleteUser}
        countries={countries}
        countriesExternalData={countriesExternalData}
        openEditDetails={openEditDetails}
        openChangeOwnerForm={openChangeOwnerForm}
        redirectPage={redirectPage}
        changeCurrency={changeCurrency}
        billingDetails={billingDetails}
        saveBillingDetails={saveBillingDetails}
        menuSize={menuSize}
        userLicence={userLicence}
        currentSubscription={currentSubscription}
        getFeaturesOfPlans={getFeaturesOfPlans}
      />
    </React.Fragment>
  );
};
